import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const BOT_LINK = "https://t.me/instant_email_bot";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png";

/**
 * Shared layout for static pages (About, Contact, Privacy, Terms).
 * Sets per-page <title>, meta description, canonical and og:url on mount.
 */
export default function LegalLayout({ title, metaTitle, metaDescription, canonicalPath, children, testid }) {
  React.useEffect(() => {
    if (metaTitle) document.title = metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc && metaDescription) desc.setAttribute('content', metaDescription);
    const href = `https://telegramemailbot.com${canonicalPath}`;
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.setAttribute('href', href);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', href);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && metaTitle) ogTitle.setAttribute('content', metaTitle);
  }, [metaTitle, metaDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid={testid}>
      <header className="bg-white border-b border-gray-200/60 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo-link">
            <img src={LOGO_URL} alt="TelegramEmailBot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-gray-900">TelegramEmailBot</span>
          </Link>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all shadow-md" data-testid="legal-header-cta">
            Get Started ($4.99)
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8" data-testid="legal-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-gray-900 mb-8 leading-tight">{title}</h1>

        <article className="legal-content font-manrope text-gray-700 space-y-5 [&_h2]:font-outfit [&_h2]:font-bold [&_h2]:text-xl [&_h2]:text-gray-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-outfit [&_h3]:font-semibold [&_h3]:text-base [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:leading-relaxed [&_a]:text-[#0088CC] [&_a:hover]:underline [&_strong]:font-semibold [&_strong]:text-gray-900 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:leading-relaxed">
          {children}
        </article>
      </main>

      <footer className="border-t border-gray-100 py-8 px-4 mt-12">
        <div className="max-w-3xl mx-auto space-y-4">
          <p className="text-xs text-gray-500 font-manrope text-center leading-relaxed max-w-2xl mx-auto" data-testid="legal-trust-note">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              Secure
            </span>
            {" — "}TelegramEmailBot does <strong className="text-gray-700">not store email passwords</strong> and only processes notifications authorized by users. All traffic is encrypted via HTTPS.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-gray-400 font-manrope pt-4 border-t border-gray-100">
            <span>&copy; 2026 TelegramEmailBot. All rights reserved.</span>
            <div className="flex flex-wrap gap-4">
              <Link to="/about" className="hover:text-gray-700 transition-colors">About</Link>
              <Link to="/contact" className="hover:text-gray-700 transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-gray-700 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-700 transition-colors">Terms</Link>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 font-manrope text-center" data-testid="legal-company-name">
            Operated by <strong className="text-gray-600">Mii Global Partner Holdings Sdn. Bhd.</strong> · Reg. No. 202601028203 (1690298-K) · Penang, Malaysia
          </p>
        </div>
      </footer>
    </div>
  );
}
